import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { scrollBody, card, ProgressHeader, WebEntryLayout } from './entryHelpers';
import { patientAuthFlow, isDemoAuthEnabled } from '../../auth/patientAuthFlow';

const CONSENT_ITEMS = [
  'Share records with Doctor',
  'Share records with Hospital',
  'Share records with Lab',
  'Share records with Pharmacy',
  'Emergency Access (Ambulance/ER)',
];

// Positionally matched to CONSENT_ITEMS — keys the backend registration payload expects.
const CONSENT_KEYS = ['doctor', 'hospital', 'lab', 'pharmacy', 'emergency'];

function ConsentToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={onToggle}
      style={{
        width: 36,
        height: 20,
        borderRadius: 10,
        background: on ? T.success : T.border,
        position: 'relative',
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'background 0.15s ease',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: 8,
          background: '#FFF',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          transition: 'left 0.15s ease',
        }}
      />
    </div>
  );
}

export default function PatRegConsentScreen() {
  const navigate = useNavigate();
  const [consents, setConsents] = useState<boolean[]>(CONSENT_ITEMS.map(() => true));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = (i: number) =>
    setConsents((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const handleNext = async () => {
    setError('');
    const consentsMap = Object.fromEntries(CONSENT_KEYS.map((key, i) => [key, consents[i]]));
    patientAuthFlow.acceptConsent(consentsMap);

    setBusy(true);
    const result = await patientAuthFlow.backendRegister();
    setBusy(false);

    if (result.success) {
      navigate('/patient/register/nhc-id');
      return;
    }

    if (isDemoAuthEnabled()) {
      // Backend unreachable — fall back to the local-only preview flow so the
      // rest of the wizard (NHC ID display, card, success) still has state to show.
      patientAuthFlow.submitRegistration();
      navigate('/patient/register/nhc-id');
      return;
    }

    setError(result.error ?? 'Could not complete registration — please check your connection and try again.');
  };

  return (
    <WebEntryLayout>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: T.white, fontFamily: T.font }}>
      <ProgressHeader title="Consent Preferences" step={7} total={8} onBack={() => navigate(-1)} />
      <div style={scrollBody}>
        <div style={card()}>
          <div style={{ fontSize: 10, lineHeight: 1.6, color: T.textSec, marginBottom: 10 }}>
            Control who can access your health records. You can change these anytime in Settings.
          </div>
          {CONSENT_ITEMS.map((label, i) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 11,
                fontWeight: 600,
                color: T.text,
                padding: '8px 0',
                borderTop: i > 0 ? `1px solid ${T.borderLight}` : 'none',
              }}
            >
              <span>{label}</span>
              <ConsentToggle on={consents[i]} onToggle={() => toggle(i)} />
            </div>
          ))}
        </div>

        {error && (
          <div style={{ fontSize: 10, color: T.danger, marginTop: 10, textAlign: 'center' }}>{error}</div>
        )}

        <button
          disabled={busy}
          onClick={handleNext}
          style={{
            width: '100%',
            padding: '14px 0',
            background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
            color: T.white,
            border: 'none',
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: T.font,
            cursor: 'pointer',
            marginTop: 6,
          }}
        >
          {busy ? 'Creating your account…' : 'Next'}
        </button>
      </div>
    </div>
    </WebEntryLayout>
  );
}
